import { defineStore } from 'pinia'
import { api } from '@/plugins/api'
import globals from '@/plugins/globals'
const baseUrl = window.config ? `${window.config.apiUrl}/user` : null

const roleId = {
  shop: ['15', '16', '17'],
  center: [],
  partner: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14']
}

// this should be an api call but for now we just hard code
const PAGE_PERMISSIONS = {
  '/login': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
  '/dashboard': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
  '/upload-estimate': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
  // Anyone who can upload has to be able to see what became of their uploads, so this matches
  // /upload-estimate exactly. The listing is scoped to the caller's own firm by the API, which takes
  // the firm from the token and never from the request.
  '/upload-estimate-review': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
  // Same audience as the PDF upload: it creates the same kind of inspection, from a VIN instead.
  '/vin-inspection': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17],
  '/locations': [1, 2, 3, 5, 7, 9, 12, 15],
  '/memberships': [1, 2, 3, 5],
  '/reports/provider-billing': [1]
}

// Pages that also need an environment feature switched on (Admin firm > Features). Any one listed
// feature is enough. Features default to off, so these pages stay hidden until a super admin enables them.
const PAGE_FEATURES = {
  '/upload-estimate': ['PdfUpload'],
  '/vin-inspection': ['VinOnlyInspection'],
  '/upload-estimate-review': ['PdfUpload', 'VinOnlyInspection']
}

function readFeatures() {
  try {
    return JSON.parse(sessionStorage.getItem('features'))
  } catch (e) {
    return null
  }
}

export const authStore = defineStore({
  id: 'auth',
  state: () => ({
    // initialize state from local storage to enable user to stay logged in
    token: JSON.parse(sessionStorage.getItem('token')),
    profile: sessionStorage.getItem('profile') ? JSON.parse(sessionStorage.getItem('profile')) : { username: null, password: null, roles: [] },
    dashboardName: sessionStorage.getItem('dashboardName') ? sessionStorage.getItem('dashboardName') : null,
    // { [name]: enabled } for the environment; null until loaded
    features: readFeatures()
  }),
  actions: {
    async login(username, password) {
      const token = await api.post(`${baseUrl}/getToken`, { username, password })
      if (token) {
        await this.processToken(token)
      }
    },
    setData(data) {
      switch (data.name) {
        case 'username':
          this.profile.username = data.value
          break
        case 'password':
          this.profile.password = data.value
          break
      }
    },
    clearToken() {
      this.token = null
      this.profile = null
      this.features = null
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('profile')
      sessionStorage.removeItem('features')
    },
    logout() {
      this.token = null
      this.profile = null
      this.features = null
      sessionStorage.clear()
      this.$router.push('/login')
    },
    // Takes the page path directly. This was previously a no-arg action that returned a closure,
    // while every caller (the router guard and NavLeft) passed the path straight in and got the
    // closure back. A function is truthy, so no entry in PAGE_PERMISSIONS was ever enforced.
    isAllowed(page = '') {
      if (PAGE_FEATURES[page] && !PAGE_FEATURES[page].some((f) => this.isFeatureEnabled(f))) {
        return false
      }
      if (!PAGE_PERMISSIONS[page]) {
        return true
      }
      if (this.profile && this.profile.roles && this.profile.roles.length) {
        return this.profile.roles.some((r) => PAGE_PERMISSIONS[page].includes(r))
      }
      return PAGE_PERMISSIONS[page].includes('Guest')
    },
    isFeatureEnabled(name) {
      return !!(this.features && this.features[name])
    },
    // Environment feature switches. A failed load leaves every feature off.
    async loadFeatures() {
      let features = {}
      try {
        const list = await api.get('feature')
        if (Array.isArray(list)) {
          list.forEach((f) => {
            features[f.name] = !!f.enabled
          })
        }
      } catch (e) {
        console.error('Features could not be loaded', e)
      }
      this.features = features
      sessionStorage.setItem('features', JSON.stringify(features))
      return features
    },
    async resetPassword(data) {
      return await api.post('user/resetPassword', data)
    },
    async updateUserParams(data) {
      return await api.post('user/updateUserParams', data)
    },
    // for SSO login
    async redeemTempToken(tempToken) {
      let token = await api.post(`${baseUrl}/redeemTempToken`, tempToken)
      if (token) {
        await this.processToken(token)
      }
    },
    async processToken(token) {
      this.token = token.slice(1, -1)
      // store the token to a session variable
      sessionStorage.setItem('token', JSON.stringify(token.slice(1, -1)))
      // decode the token
      var base64Url = token.split('.')[1]
      var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      var jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          })
          .join('')
      )
      // parse the payload
      var tokenPayload = JSON.parse(jsonPayload)
      // establish a profile
      let profile = {
        user: parseInt(tokenPayload.uid),
        contact: parseInt(tokenPayload.cid),
        firm: parseInt(tokenPayload.fid),
        firmName: tokenPayload.fn,
        timezone: parseInt(tokenPayload.tz),
        firstName: tokenPayload.fname,
        lastName: tokenPayload.lname,
        username: tokenPayload.uname,
        email: tokenPayload.email,
        language: tokenPayload.cc,
        type: parseInt(tokenPayload.ct),
        firmType: parseInt(tokenPayload.bt),
        guid: tokenPayload.guid,
        groups: tokenPayload.gids,
        roles: [],
        firmId: tokenPayload.pid,
        connectClass: tokenPayload.fcc ? parseInt(tokenPayload.fcc) : null,
        insuranceCount: tokenPayload.ic ? parseInt(tokenPayload.ic) : 0
      }
      profile.roles.push(parseInt(tokenPayload.rid))
      // save the profile to a session variable
      sessionStorage.setItem('profile', JSON.stringify(profile))
      this.profile = profile
      if (roleId['shop'].includes(tokenPayload.rid)) {
        sessionStorage.setItem('firm', profile.firm)
        this.dashboardName = 'collision'
      } else if (roleId['partner'].includes(tokenPayload.rid)) {
        this.dashboardName = 'repairnetwork'
      } else if (roleId['center'].includes(tokenPayload.rid)) {
        this.dashboardName = 'calibration'
      }
      sessionStorage.setItem('dashboardName', this.dashboardName)
      await this.loadFeatures()
      let response = await api.get(`firm/preferences?firm=${profile.firm}&category=General`)
      if (response) {
        if (response !== '') {
          console.log(response)
          response.map((item) => {
            if (item.name == 'Logo') {
              sessionStorage.setItem('Logo', item.value)
              return item.value
            }
          })
        }
      }
    }
  }
})
